/*
 * DATA_RIGHTS: HONEYWELL CONFIDENTIAL AND PROPRIETARY
 * THIS WORK CONTAINS VALUABLE CONFIDENTIAL AND PROPRIETARY
 * INFORMATION. DISCLOSURE, USE OR REPRODUCTION OUTSIDE OF
 * HONEYWELL INTERNATIONAL, INC. IS PROHIBITED EXCEPT AS
 * AUTHORIZED IN WRITING. THIS UNPUBLISHED WORK IS PROTECTED BY
 * THE LAWS OF THE UNITED STATES AND OTHER COUNTRIES. IN THE
 * EVENT OF PUBLICATION, THE FOLLOWING NOTICE SHALL APPLY:
 * Copyright (C) 2022 HONEYWELL INTERNATIONAL, INC., ALL RIGHTS RESERVED.
 *
 * Created by H526448 on 2/28/2024.
 */

import React from 'react';
import {ACCESS, ACTION_TYPES, CORRECTIVE_ACTION_STATE, FAULT_STATES, isClosedState} from "./FaultsConst";
import {Grid} from "@scuf/common";
import {FormattedMessage} from "react-intl";

// Both ids are used only in the JavaScript and used only for distinction between them.
// NEW_ACTION_ID represents id for row that allows to create new Test/CA for the Fault.
export const NEW_ACTION_ID = "NEW"
// EXISTING_ACTION_ID represents id for row that allows us to execute Test/CA that was not allocated for the Fault.
export const EXISTING_ACTION_ID = "EXISTING"

export const getReadOnlyStatus = (props) => {
    const {fault, intl} = props;
    const assignedUser = fault.assignedUser !== null && fault.assignedUser.id === props.security.userProfile.id ? fault.assignedUser : undefined;
    let text = '';
    if (!isClosedState(fault.faultConditionState) && (!assignedUser || fault.access !== ACCESS.WRITE)) {
        text = intl.formatMessage({id: "fault.detail.label.readonly"})
    }
    return <div className='fault-readonly-text'>{text}</div>
};

/**
 * Check if the fault is in appropriate state to show summary of performed actions.
 */
export const verifySummaryState = (props) => {
    const {fault} = props;
    const faultStates = [FAULT_STATES.CLOSED_RESOLVED, FAULT_STATES.CLOSED_DUE_TO_LDI_IMPORT, FAULT_STATES.CLOSED_DUE_TO_INACTIVITY, FAULT_STATES.VERIFICATION]
    return faultStates.includes(fault.details.faultConditionState) && fault.actions
};

/**
 *  Check if the fault is in the immutable/final state (closed or deleted).
 */
export const immutableFaultState = (fault) => {
    const immutableStates = [FAULT_STATES.CLOSED_RESOLVED, FAULT_STATES.CLOSED_DUE_TO_INACTIVITY, FAULT_STATES.CLOSED_DUE_TO_LDI_IMPORT, FAULT_STATES.DELETED]
    return immutableStates.includes(fault.details.faultConditionState)
}

/**
 * Renders one row for one CorrectiveAction/Test in the fault summary.
 */
const getPerformedActionRow = (action) => {
    const actionResult = action.type === ACTION_TYPES.TEST ?
        action.testResults.filter(testResults => testResults.id.toString() === action.currentResult)[0].name
        :
        <FormattedMessage id={"fault.troubleshoot.ca.state." + action.currentResult} />
    return (
        <Grid.Row key={action.id}>
            <Grid.Column width={8}>
                <li>{action.name}</li>
            </Grid.Column>
            <Grid.Column width={4}>
                <li>{actionResult}</li>
            </Grid.Column>
        </Grid.Row>
    )
}

export const getClosingReasonFromComments = (props, predefinedReasons) => {
    const {intl, fault} = props;
    let closingReason = '';
    fault.comments.map(comment => {
        const commentPart = comment.text.split(/([.]|:)/)[0]
        if (predefinedReasons.includes(commentPart)) {
            closingReason = commentPart;
        }
        if (commentPart === intl.formatMessage({id: "fault.troubleshoot.summary.info.additionalAction"})) {
            closingReason = commentPart;
        }
    })
    return closingReason
}

/**
 * Check & Renders the closing reason of the fault in case of fault is closed by predefined reason.
 * The closing reason is received as argument to avoid multiple fetching of closing predefined reasons & checking the same with selected reason.
 */
const getFaultClosingDetails = (props, closingReason) => {
    const {intl, fault} = props
    if (fault.details === null || fault.details.faultConditionState !== 'CLOSED_RESOLVED' || closingReason === null || closingReason === '') {
        return null
    }
    return (
        <Grid>
            <Grid.Row>
                <Grid.Column width={8}>
                    <h4>{intl.formatMessage({id: "fault.detail.summary.closedTitle"})}</h4>
                </Grid.Column>
                <Grid.Column width={4}>
                    <h4>{intl.formatMessage({id: "fault.detail.summary.reasonTitle"})}</h4>
                </Grid.Column>
            </Grid.Row>
            <ul className="performed-actions-list">
                <Grid.Row>
                    <Grid.Column width={8}>
                        <li>{intl.formatMessage({id: "fault.detail.summary.closedDueToPredefinedReason"})}</li>
                    </Grid.Column>
                    <Grid.Column width={4}>
                        <li>{closingReason}</li>
                    </Grid.Column>
                </Grid.Row>
            </ul>
        </Grid>
    )
}

/**
 * Renders the summary of performed actions. Used on the Summary page, and it's displayed on the main Fault page if
 * the Fault is in the Verification state or is Closed.
 */
export const getFaultSummary = (props, closingReason) => {
    const {intl, fault} = props

    if (fault.actions === null) {
        return null
    }

    const correctiveActions = fault.actions
        .filter(action => action.type === ACTION_TYPES.CORRECTIVE_ACTION && action.currentResult !== CORRECTIVE_ACTION_STATE.NOT_PERFORMED)
        .sort(action => { return action.currentResult === CORRECTIVE_ACTION_STATE.FIXED ? 1 : -1 })

    const CAs = correctiveActions.length > 0 ? correctiveActions.map(action => getPerformedActionRow(action))
        :
        <Grid.Row>
            <Grid.Column width={8}>
                { intl.formatMessage({id: "fault.troubleshoot.action.none_performed"}) }
            </Grid.Column>
        </Grid.Row>

    const testsPerformed = fault.actions.filter(action => action.type === ACTION_TYPES.TEST && action.currentResult !== null)

    const tests = testsPerformed.length > 0 ? testsPerformed.map(action => getPerformedActionRow(action))
        :
        <Grid.Row>
            <Grid.Column width={8}>
                { intl.formatMessage({id: "fault.troubleshoot.action.none_performed"}) }
            </Grid.Column>
        </Grid.Row>

    return (
        <div>
            <Grid>
                <Grid.Row>
                    <Grid.Column width={8}>
                        <h4>{intl.formatMessage({id: "fault.detail.summary.tests"})}</h4>
                    </Grid.Column>
                    <Grid.Column width={4}>
                        <h4>{intl.formatMessage({id: "fault.detail.summary.results"})}</h4>
                    </Grid.Column>
                </Grid.Row>
                <ul className="performed-actions-list">{tests}</ul>
                <Grid.Row>
                    <Grid.Column width={8}>
                        <h4>{intl.formatMessage({id: "fault.detail.summary.actions"})}</h4>
                    </Grid.Column>
                    <Grid.Column width={4}>
                        <h4>{intl.formatMessage({id: "fault.detail.summary.results"})}</h4>
                    </Grid.Column>
                </Grid.Row>
                <ul className="performed-actions-list">{CAs}</ul>
            </Grid>
            {getFaultClosingDetails(props,closingReason)}
        </div>
    )
};
